/**
 * BuddyGroupPendingInvitationsCard
 *
 * Top-of-dashboard action card promoting pending buddy-group invitations
 * awaiting the viewer's accept/reject. Parallels PendingInvitationsCard
 * (team-roping partner invites) so users see and resolve every kind of
 * invitation the moment they sign in instead of having to navigate into
 * /my/buddy-groups.
 *
 * Why this exists: invitations on the My Buddy Groups page were easy to
 * miss for users who don't visit that page often. Surfacing them on the
 * dashboard puts the action item front-and-centre, matching the existing
 * team-roping banner's behaviour.
 *
 * Implementation notes:
 *   - Reuses the SAME hooks the BuddyGroupsPage uses (usePendingInvitations,
 *     useAcceptInvitation, useDeclineInvitation) so accept/decline is wired
 *     to the same mutation pipeline and React Query invalidations keep both
 *     surfaces in sync automatically.
 *   - Profile gate mirrors BuddyGroupsPage: accept is gated on a full
 *     profile (joining a group lets the primary enter you into events that
 *     require full demographic data); decline is never gated so a user
 *     with a half-filled profile can still back out of an accidental
 *     invite. returnTo bounces them back to the dashboard after saving.
 *   - Visual palette is violet to match the buddy-group convention used
 *     across the rest of the app, keeping it distinct from the amber
 *     team-roping banner when both render at once.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Crown, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  usePendingInvitations,
  useAcceptInvitation,
  useDeclineInvitation,
  type PendingInvitation,
} from '@/hooks/useBuddyGroupInvitations';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useToast } from '@/contexts/ToastContext';
import { useNotifications } from '@/hooks/useNotifications';
import { isFullProfileComplete } from '@/utils/profileCompleteness';

const VISIBLE_LIMIT = 5;

export function BuddyGroupPendingInvitationsCard() {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const userProfileId = profile?.id ?? '';
  const { showSuccess, showError } = useToast();
  const { refresh: refreshNotifications } = useNotifications();
  const { data, isLoading } = usePendingInvitations(userProfileId);
  const acceptMutation = useAcceptInvitation();
  const declineMutation = useDeclineInvitation();
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  if (isLoading) return null;
  const invitations = data ?? [];
  if (invitations.length === 0) return null;

  const visible = invitations.slice(0, VISIBLE_LIMIT);
  const overflow = invitations.length - visible.length;

  const handleAccept = async (invitation: PendingInvitation) => {
    // Mirror the BuddyGroupsPage profile gate — accepting an invitation
    // lets the primary register you into events that need full
    // demographic data, so we want the full profile before they're on
    // the hook. Declines aren't gated.
    if (!isFullProfileComplete(profile as Record<string, unknown> | null)) {
      showError(
        'Complete your profile first',
        'You need a full profile before you can accept group invitations.',
      );
      navigate('/profile?returnTo=/dashboard');
      return;
    }
    setActionLoadingId(invitation.invitationId);
    try {
      await acceptMutation.mutateAsync(invitation.invitationId);
      showSuccess(
        'Invitation accepted',
        `You're now a member of ${invitation.groupName ?? 'the group'}.`,
      );
      refreshNotifications();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDecline = async (invitation: PendingInvitation) => {
    setActionLoadingId(invitation.invitationId);
    try {
      await declineMutation.mutateAsync(invitation.invitationId);
      showSuccess(
        'Invitation declined',
        invitation.invitedByName
          ? `${invitation.invitedByName} has been notified.`
          : 'The inviter has been notified.',
      );
      refreshNotifications();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Card
      className="border-violet-300 bg-violet-50 dark:border-violet-900/60 dark:bg-violet-950/30"
      role="region"
      aria-label="Pending buddy group invitations awaiting your response"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-violet-200/80 dark:bg-violet-900/50 p-2 motion-safe:animate-pulse">
            <Users
              className="h-5 w-5 text-violet-700 dark:text-violet-300"
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-violet-900 dark:text-violet-100">
              {invitations.length === 1
                ? 'You have a pending buddy group invitation'
                : `You have ${invitations.length} pending buddy group invitations`}
            </h3>
            <p className="text-sm text-violet-800/90 dark:text-violet-200/90 mt-0.5 leading-relaxed">
              Approve or decline below — buddy groups let the primary
              cardholder enter you into events together.
            </p>
          </div>
        </div>

        <ul className="space-y-2 pl-1" role="list">
          {visible.map((inv) => {
            const isBusy = actionLoadingId === inv.invitationId;
            const groupName = inv.groupName ?? 'Unnamed group';
            const primary = inv.members.find((m) => m.isPrimary);
            // Show every non-primary member by display name so the
            // invitee can recognise who they'd be joining. Falls back
            // to a count if names aren't populated (defensive — the
            // backend usually enriches these).
            const otherMembers = inv.members.filter((m) => !m.isPrimary);
            const memberSummary = otherMembers.length
              ? otherMembers
                  .map((m) => m.displayName)
                  .filter(Boolean)
                  .slice(0, 3)
                  .join(', ')
              : null;
            const extraMembers =
              otherMembers.length > 3 ? otherMembers.length - 3 : 0;

            return (
              <li
                key={inv.invitationId}
                className="rounded-md border border-violet-200/70 dark:border-violet-900/40 bg-white/70 dark:bg-violet-950/20 px-3 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">
                      {groupName}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {[
                      inv.invitedByName
                        ? `invited by ${inv.invitedByName}`
                        : 'invited by a group member',
                      primary?.displayName
                        ? `primary: ${primary.displayName}`
                        : null,
                      memberSummary
                        ? `with ${memberSummary}${extraMembers ? ` +${extraMembers} more` : ''}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {/* Privacy disclosure — accepting the invite shares
                      your email with the rest of the group. Surfaced
                      before Accept so consent is informed. Mirrors the
                      same note shown in PendingInvitationsList on
                      /my/buddy-groups. */}
                  <p className="text-xs flex items-start gap-1 text-violet-800/90 dark:text-violet-200/90">
                    <ShieldAlert
                      className="h-3 w-3 mt-0.5 shrink-0 text-amber-500"
                      aria-hidden="true"
                    />
                    <span>
                      Accepting will share your email with this group's
                      members.
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleDecline(inv)}
                    disabled={isBusy}
                    aria-label={`Decline invitation to ${groupName}`}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-violet-700 hover:bg-violet-800 text-white"
                    onClick={() => handleAccept(inv)}
                    disabled={isBusy}
                    aria-label={`Approve invitation to ${groupName}`}
                  >
                    {isBusy ? 'Approving…' : 'Approve'}
                  </Button>
                </div>
                {/* Off-screen tag for primary indicator so the icon
                    above conveys identity even without colour cues. */}
                {primary && (
                  <span className="sr-only">
                    <Crown aria-hidden="true" /> Primary cardholder:{' '}
                    {primary.displayName ?? 'unnamed member'}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {overflow > 0 && (
          <p className="text-xs text-muted-foreground pl-1">
            And {overflow} more —{' '}
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={() => navigate('/my/buddy-groups')}
            >
              open My Buddy Groups
            </button>{' '}
            to act on the rest.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
